// Single source of truth for Call Review Sheet scoring math — shared by the
// live React table (CallReviewTable.js) and the native PDF export
// (generateCallReviewAutoTable.js) so both always render identical numbers.

export const USING_TOOLS_FIELDS = [
  "Did the AC use the intro script?",
  "Did the AC attempt the 4 W's - What's going on?",
  "Did the AC attempt the 4 W's - How long has it been going on for?",
  "Did the AC attempt the 4 W's - What started this?",
  "Did the AC attempt the 4 W's - Why now?",
  "Was the AC in control of the call?",
  "Did AC ask for referral source?",
];

export const SALES_FIELDS = [
  "Did the AC understand wants and needs?",
  "Did the AC personalize the pitch?",
  "Did the AC show empathy?",
];

export const OBJECTIONS_FIELDS = [
  "Did the AC initiate the assessment process on the call?",
  "Did the AC explain insurance properly?",
  "Did the AC handle objections well?",
  "Did the AC create urgency?",
  "Did the AC schedule next steps?",
];

export const mapUT2Values = (ut2) => {
  if (!ut2) return [0, 0, 0, 0];
  if (Array.isArray(ut2)) return ut2.map((v) => v ?? 0);
  if (typeof ut2 === "object") {
    return [ut2[1] ?? 0, ut2[2] ?? 0, ut2[3] ?? 0, ut2[4] ?? 0];
  }
  return [0, 0, 0, 0];
};

export const getTotal = (arr) =>
  arr.reduce((sum, val) => sum + (Number(val) || 0), 0);

/**
 * Derives every value CallReviewTable renders — summary rows plus the three
 * detail sections (Using the Tools / Sales / Objections) — from the raw
 * callReviewData API payload. Returns null if there's nothing to derive.
 */
export const deriveCallReviewSections = (callReviewData) => {
  if (!callReviewData) return null;

  const {
    UT1,
    UT2,
    UT3,
    UT4,
    S1,
    S2,
    S3,
    O1,
    O2,
    O3,
    O4,
    O5,
    sections = {},
    totals = {},
    average = {},
    weightage = {},
  } = callReviewData;

  const utSection = sections.UT ?? {};
  const sSection = sections.S ?? {};
  const oSection = sections.O ?? {};

  const wf = (key, defaultMaxRaw = 1) => {
    const factor = weightage[key]?.factor ?? 1;
    const maxRaw = weightage[key]?.maxRaw ?? defaultMaxRaw;
    return { factor, maxScore: factor * maxRaw };
  };

  const wfUT1 = wf("UT1");
  const wfUT2 = wf("UT2");
  const wfUT3 = wf("UT3");
  const wfUT4 = wf("UT4", 2);
  const wfS1 = wf("S1");
  const wfS2 = wf("S2");
  const wfS3 = wf("S3");
  const wfO1 = wf("O1");
  const wfO2 = wf("O2");
  const wfO3 = wf("O3");
  const wfO4 = wf("O4");
  const wfO5 = wf("O5");

  const utFactors = [
    wfUT1.factor,
    wfUT2.factor,
    wfUT2.factor,
    wfUT2.factor,
    wfUT2.factor,
    wfUT3.factor,
    wfUT4.factor,
  ];
  const utMaxValues = [
    wfUT1.maxScore,
    wfUT2.maxScore,
    wfUT2.maxScore,
    wfUT2.maxScore,
    wfUT2.maxScore,
    wfUT3.maxScore,
    wfUT4.maxScore,
  ];

  const sFactors = [wfS1.factor, wfS2.factor, wfS3.factor];
  const sMaxValues = [wfS1.maxScore, wfS2.maxScore, wfS3.maxScore];

  const oFactors = [
    wfO5.factor,
    wfO1.factor,
    wfO2.factor,
    wfO3.factor,
    wfO4.factor,
  ];
  const oMaxValues = [
    wfO5.maxScore,
    wfO1.maxScore,
    wfO2.maxScore,
    wfO3.maxScore,
    wfO4.maxScore,
  ];

  const usingToolsValues = [UT1 ?? 0, ...mapUT2Values(UT2), UT3 ?? 0, UT4 ?? 0];
  const salesValues = [S1 ?? 0, S2 ?? 0, S3 ?? 0];
  const objectionsValues = [O5 ?? 0, O1 ?? 0, O2 ?? 0, O3 ?? 0, O4 ?? 0];

  const totalScore5 =
    (utSection.outOf5 ?? 0) + (sSection.outOf5 ?? 0) + (oSection.outOf5 ?? 0);

  return {
    summary: { utSection, sSection, oSection, totals, average, totalScore5 },
    usingTools: {
      title: "Using the Tools",
      fields: USING_TOOLS_FIELDS,
      values: usingToolsValues,
      maxValues: utMaxValues,
      weightageFactor: utFactors,
    },
    sales: {
      title: "Sales",
      fields: SALES_FIELDS,
      values: salesValues,
      maxValues: sMaxValues,
      weightageFactor: sFactors,
    },
    objections: {
      title: "Objections",
      fields: OBJECTIONS_FIELDS,
      values: objectionsValues,
      maxValues: oMaxValues,
      weightageFactor: oFactors,
    },
  };
};
